import type { PostHogConfig, PostHogInterface } from "posthog-js";
import type { FeatureFlag } from "@paladin/common";

import { useFeatureFlagResult } from "@posthog/react";
import { captureException } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { posthog } from "posthog-js";

import { isPaladinStorybook } from "~/pld/lib/storybook";

function applyPaladinE2eFeatureFlagOverrides(
  postHogClient: PostHogInterface,
): void {
  const featureFlagsOverrides = window.__PALADIN_E2E_FEATURE_FLAGS__;
  if (!featureFlagsOverrides) {
    return;
  }

  postHogClient.featureFlags.overrideFeatureFlags(featureFlagsOverrides);
}

const POSTHOG_OPTIONS: Partial<PostHogConfig> = {
  ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST,
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_API_HOST,
  defaults: "2025-05-24" as const,
  capture_exceptions: false,
  debug: false,
  // Deployed environments only, the back-end follows the same rule. Each one
  // sends to its own PostHog project.
  autocapture:
    import.meta.env.MODE === "dev" ||
    import.meta.env.MODE === "staging" ||
    import.meta.env.MODE === "production",
  disable_session_recording: import.meta.env.DEV,
  loaded: function (postHogClient) {
    if (import.meta.env.DEV) {
      postHogClient.opt_out_capturing();
    }
    applyPaladinE2eFeatureFlagOverrides(postHogClient);
  },
  mask_all_text: true,
};

let isPostHogInitialized = false;

export function getPostHogClient() {
  if (typeof window !== "undefined" && !isPostHogInitialized) {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, POSTHOG_OPTIONS);
    isPostHogInitialized = true;
  }

  return posthog;
}

export const POSTHOG_PERSON_PROPERTY = {
  COMPANY_BRANCH_ID: "company_branch_id",
  COMPANY_BRANCH_NAME: "company_branch_name",
  // Targets the flags of a whole service, such as the nursing module.
  OCCUPATIONAL_HEALTH_SERVICE_ID: "occupational_health_service_id",
} as const;

type CompanyBranchIdentity = {
  id: string;
  name: string;
};

type ViewerWithCompanyBranches = {
  companyBranchRepresentatives: ReadonlyArray<{
    companyBranch: CompanyBranchIdentity;
  }>;
  patient: {
    contracts: ReadonlyArray<{
      companyBranch: CompanyBranchIdentity;
    }>;
  } | null;
};

export function companyBranchForPostHogIdentify(
  viewer: ViewerWithCompanyBranches,
  activeCompanyBranchId: string | null | undefined,
): CompanyBranchIdentity | undefined {
  const branchesById = new Map<string, CompanyBranchIdentity>();

  for (const representative of viewer.companyBranchRepresentatives) {
    const branch = representative.companyBranch;
    branchesById.set(branch.id, { id: branch.id, name: branch.name });
  }

  const contracts = viewer.patient?.contracts ?? [];
  for (const contract of contracts) {
    const branch = contract.companyBranch;
    if (!branchesById.has(branch.id)) {
      branchesById.set(branch.id, { id: branch.id, name: branch.name });
    }
  }

  if (activeCompanyBranchId !== undefined && activeCompanyBranchId !== null) {
    const activeBranch = branchesById.get(activeCompanyBranchId);
    if (activeBranch !== undefined) {
      return activeBranch;
    }
  }

  return branchesById.values().next().value;
}

export function identifyPostHogUser(
  postHogClient: Pick<PostHogInterface, "identify">,
  user: {
    id: string;
    email: string;
    name: string;
    companyBranch?: CompanyBranchIdentity;
    occupationalHealthServiceId?: string;
  },
): void {
  const companyBranchProperties =
    user.companyBranch === undefined
      ? {}
      : {
          [POSTHOG_PERSON_PROPERTY.COMPANY_BRANCH_ID]: user.companyBranch.id,
          [POSTHOG_PERSON_PROPERTY.COMPANY_BRANCH_NAME]:
            user.companyBranch.name,
        };

  const occupationalHealthServiceProperties =
    user.occupationalHealthServiceId === undefined
      ? {}
      : {
          [POSTHOG_PERSON_PROPERTY.OCCUPATIONAL_HEALTH_SERVICE_ID]:
            user.occupationalHealthServiceId,
        };

  postHogClient.identify(user.id, {
    email: user.email,
    name: user.name,
    ...companyBranchProperties,
    ...occupationalHealthServiceProperties,
  });
}

export function resetPostHogUser(): void {
  const postHogClient = getPostHogClient();
  postHogClient.reset();

  // `reset()` clears consent and returns to the SDK default, which captures.
  // Dev opts out in `loaded`; a logout must not undo that.
  if (import.meta.env.DEV) {
    postHogClient.opt_out_capturing();
  }

  // `reset()` drops the in-memory flag overrides e2e tests install.
  applyPaladinE2eFeatureFlagOverrides(postHogClient);
}

export function getPostHogFeatureFlag(
  featureFlag: FeatureFlag,
  options?: {
    /**
     * Resolve `false` if PostHog has not answered within this many milliseconds.
     *
     * `onFeatureFlags` does not fire at all when PostHog cannot be reached — an
     * ad blocker, a proxy, a dead `api_host` — and it reports an error only when
     * PostHog itself says one occurred, so without this the promise never
     * settles and the listener below is never torn down. Callers that can wait
     * (a component rendering a pending state) omit it; a caller that blocks on
     * the answer passes one.
     */
    timeoutMs?: number;
  },
): Promise<boolean | string> {
  return new Promise((resolve, reject) => {
    const postHogClient = getPostHogClient();

    // `onFeatureFlags` fires on every flag refresh, not just the first one, and
    // returns an unsubscribe handle. We only care about the first resolution,
    // so we tear the listener down as soon as it settles to avoid leaking a
    // subscription that keeps firing for the lifetime of the app.
    let settled = false;
    // Initialised to a no-op: when flags are already cached, PostHog invokes
    // the callback synchronously *before* `onFeatureFlags` returns, so the real
    // handle isn't assigned yet at that point. The post-subscription check
    // below unsubscribes in that case; the async case unsubscribes here.
    let unsubscribe = () => {};
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };

    unsubscribe = postHogClient.onFeatureFlags(
      (_flags, flagVariants, context) => {
        // Per what PostHog team told me, missing context should NOT be inferred
        // as an error
        if (context?.errorsLoading === true) {
          reject(new Error("PostHog feature flag resolved with an error"));
          cleanup();
          return;
        } else {
          // TS is a little bit drunk here, so I need to type it manually,
          // because `featureFlag` CAN totally be a missing key in the
          // `flagVariants` record and lead to an `undefined` value
          const flagValue: string | boolean | undefined =
            flagVariants[featureFlag];

          // PostHog doesn't return disabled feature flags, leading to `undefined`
          // We want to coerce disabled feature flags to `false`
          // As if: if the flag is disabled, it is de facto not enabled
          if (flagValue === undefined) {
            resolve(false);
            cleanup();
            return;
          }

          // And no we get the right TS types inferred
          resolve(flagValue);
          cleanup();
          return;
        }
      },
    );

    // If the callback already fired synchronously (flags were cached), it ran
    // before `unsubscribe` was assigned, so unsubscribe now that we hold it.
    if (settled) {
      unsubscribe();
      return;
    }

    if (options?.timeoutMs !== undefined) {
      timeoutId = setTimeout(() => {
        // A flag that cannot be read is not an enabled flag.
        resolve(false);
        cleanup();
      }, options.timeoutMs);
    }
  });
}

const FEATURE_FLAG_TIMEOUT_MS = 3_000;

/**
 * An unreachable PostHog never answers at all, and a loader that waits for it
 * blocks the navigation: the page sits on its loading state instead of
 * redirecting. A component can afford to stay pending, so the cap lives here
 * rather than in `getPostHogFeatureFlag`'s default.
 */
export async function readLoaderFeatureFlag(
  featureFlag: FeatureFlag,
): Promise<boolean> {
  try {
    const flagValue = await getPostHogFeatureFlag(featureFlag, {
      timeoutMs: FEATURE_FLAG_TIMEOUT_MS,
    });

    return flagValue === true;
  } catch {
    return false;
  }
}

/**
 * Can either return true/false OR the variant name of the feature flag.
 */
export function usePostHogFeatureFlag(featureFlag: FeatureFlag) {
  const isStorybook = isPaladinStorybook();

  const {
    data: isFlagEnabled,
    isPending: isFlagPending,
    isError: isFlagError,
    error: flagError,
  } = useQuery({
    queryKey: ["feature-flags", featureFlag],
    queryFn: () => getPostHogFeatureFlag(featureFlag),
    enabled: !isStorybook,
  });

  // PostHog is not wired up in Storybook. Stories can seed flag values via
  // `storybookQueryClient.setQueryData(["feature-flags", flag], value)`.
  if (isStorybook) {
    return {
      isFlagEnabled: isFlagEnabled ?? false,
      isFlagPending: false,
      isFlagError: false,
    } as const;
  }

  if (isFlagError) {
    captureException(flagError);

    return {
      isFlagEnabled: false,
      isFlagPending: false,
      isFlagError: true,
    } as const;
  }

  if (isFlagPending) {
    return {
      isFlagEnabled: undefined,
      isFlagPending: true,
      isFlagError: false,
    } as const;
  }

  return {
    isFlagEnabled,
    isFlagPending: false,
    isFlagError: false,
  } as const;
}

/**
 * Returns the payload if the feature flag is resolved and enabled.
 * (can be `undefined` if there are no payload defined in PostHog)
 *
 * Returns `null` if the feature flag is:
 * - resolved but is NOT enabled, or
 * - pending, or
 * - errored
 *
 * HINT: You can use `usePostHogFeatureFlag` to get the pending / error state.
 */
export function usePostHogFeatureFlagPayload(featureFlag: FeatureFlag) {
  const result = useFeatureFlagResult(featureFlag);

  const { isFlagEnabled, isFlagPending, isFlagError } =
    usePostHogFeatureFlag(featureFlag);

  if (
    !isFlagPending &&
    !isFlagError &&
    isFlagEnabled &&
    result?.enabled &&
    result?.key === featureFlag
  ) {
    return result.payload;
  }

  return null;
}
